import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../api-urls";

// Function to get Auth Token
const getAuthToken = () => {
  const token = localStorage.getItem("authToken");
  console.log("Auth Token:", token); // Debugging
  return token ? `Bearer ${token}` : null;
};

// Fetch user favourites
export const getUserFavourites = createAsyncThunk(
  "favourites/getUserFavourites",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No auth token found");
        return rejectWithValue("No auth token found");
      }

      const response = await axios.get(API_ENDPOINTS.favourite, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      console.log("Successfully fetched user favourites", response.data);
      return response.data || []; // Ensure a default empty array is returned
    } catch (error) {
      console.error("Error fetching favourites:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add product to favourites
export const addToFavourites = createAsyncThunk(
  "favourites/addToFavourites",
  async (productId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No auth token found");
        return rejectWithValue("No auth token found");
      }

      const response = await axios.post(
        `${API_ENDPOINTS.favourite}${productId}`,
        {},
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Product added to favourites successfully", response.data);
      return response.data;
    } catch (error) {
      console.error("Error adding product to favourites:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Remove product from favourites
export const removeFromFavourites = createAsyncThunk(
  "favourites/removeFromFavourites",
  async (productId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No auth token found");
        return rejectWithValue("No auth token found");
      }

      const response = await axios.post(
        `${API_ENDPOINTS.favourite}${productId}`,
        {},
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Product removed from favourites successfully", response.data);
      return productId; // Return only the ID to remove it from Redux state
    } catch (error) {
      console.error("Error removing product from favourites:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Define the Redux slice
const favouritesSlice = createSlice({
  name: "favourites",
  initialState: {
    items: [], // Default empty array to avoid undefined errors
    clickedItem: null, // Store the clicked item
    status: "idle",
    error: null,
  },
  reducers: {
    setClickedItem: (state, action) => {
      state.clickedItem = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserFavourites.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserFavourites.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload || []; // Ensure items is always an array
      })
      .addCase(getUserFavourites.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(addToFavourites.fulfilled, (state, action) => {
        state.items = [...state.items, action.payload]; // Add new item to list
      })
      .addCase(removeFromFavourites.fulfilled, (state, action) => {
        state.items = state.items.filter(
          (item) => item.id !== action.payload // Remove item by ID
        );
      });
  },
});

// Selector for clicked item
export const selectClickedItem = (state) => state.favourites?.clickedItem || null;

// Export actions
export const { setClickedItem } = favouritesSlice.actions;

// Export reducer
export default favouritesSlice.reducer;
